import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronsRight } from '@fortawesome/pro-regular-svg-icons';
import { toast } from 'react-toastify';
import type {
  BulkCreateMissingTimesheetInput,
  BulkSubmitMissingTimesheetsInput,
  BulkSubmitMissingTimesheetsResult,
} from '@core/timesheetApproval/missingTimesheet.types';
import { pluralize } from '@core/util/utils';
import Button from '@/components/Button';
import MissingTimesheetCard from '@/modules/timesheets/components/MissingTimesheetCard';
import TimesheetDrawerParties from '@/modules/timesheets/components/TimesheetDrawerParties';
import {
  buildMissingTimesheetSubmission,
  missingTimesheetKey,
  resolveMissingTimesheetTimes,
  type MissingTimesheetGroup,
  type MissingTimesheetTimes,
} from '@/modules/timesheets/utils/missingTimesheets.utils';
import {
  formatTimesheetDateRange,
  type PayPeriod,
} from '@/modules/timesheets/utils/timesheetGroups.utils';

interface MissingTimesheetDrawerProps {
  group: MissingTimesheetGroup | null;
  /** The pay period selected on the page, shown in the heading. */
  payPeriod: PayPeriod;
  isOpen: boolean;
  showClientName: boolean;
  canSubmit: boolean;
  onClose: () => void;
  /** Submits worked shifts and shifts marked as did not work. */
  onSubmitShifts: (
    submission: BulkSubmitMissingTimesheetsInput,
  ) => Promise<BulkSubmitMissingTimesheetsResult>;
}

const MissingTimesheetDrawer = ({
  group,
  payPeriod,
  isOpen,
  showClientName,
  canSubmit,
  onClose,
  onSubmitShifts,
}: MissingTimesheetDrawerProps) => {
  const [editingShiftKey, setEditingShiftKey] = useState<string | null>(null);
  const [editedTimesByKey, setEditedTimesByKey] = useState<
    Record<string, MissingTimesheetTimes>
  >({});
  const [didNotWorkKeys, setDidNotWorkKeys] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEditingShiftKey(null);
    setEditedTimesByKey({});
    setDidNotWorkKeys(new Set());
  }, [group?.rowKey, isOpen]);

  const submittableItems =
    group?.items.filter((item) => Boolean(item.shiftDate)) ?? [];

  const submitAll = async () => {
    const notWorked: { jobId: string; dateISO: string }[] = [];
    const worked = submittableItems.flatMap((item) => {
      const key = missingTimesheetKey(item);
      if (didNotWorkKeys.has(key)) {
        notWorked.push({ jobId: item.jobId, dateISO: item.shiftDate });
        return [];
      }
      const times = resolveMissingTimesheetTimes(item, editedTimesByKey[key]);
      const input = buildMissingTimesheetSubmission(
        item,
        times.startTime,
        times.endTime,
        times.breakMinutes,
      );
      return input ? [{ ...input, dateISO: item.shiftDate }] : [null];
    });
    if (worked.some((input) => input === null)) {
      toast.error(
        'Some shifts have no check-in or check-out time. Edit them before submitting.',
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const { submittedCount, attemptedCount } = await onSubmitShifts({
        worked: worked.filter(
          (input): input is BulkCreateMissingTimesheetInput => input !== null,
        ),
        notWorked,
      });
      if (submittedCount === attemptedCount && attemptedCount > 0) {
        toast.success(
          `${submittedCount} ${submittedCount === 1 ? 'timesheet' : 'timesheets'} submitted`,
        );
      } else if (submittedCount > 0) {
        toast.warning(
          `${submittedCount} of ${attemptedCount} timesheets submitted. Please review the rest.`,
        );
      } else {
        toast.error('Failed to submit timesheets');
      }
    } catch (error) {
      console.error('Error bulk submitting missing timesheets:', error);
      const message = error instanceof Error ? error.message : undefined;
      toast.error(message ?? 'Unable to submit timesheets');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition show={isOpen && !!group} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/5" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={React.Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  {group && (
                    <div className="relative flex h-full flex-col bg-gray-50 shadow-xl">
                      <div className="border-b border-gray-200 bg-white px-6 py-4">
                        <button
                          type="button"
                          aria-label="Close"
                          className="cursor-pointer rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                          onClick={onClose}
                        >
                          <FontAwesomeIcon
                            icon={faChevronsRight}
                            className="ml-1 h-3 w-3"
                          />
                        </button>
                        <TimesheetDrawerParties
                          userId={group.userId}
                          workerName={group.workerName}
                          workerProfileImageUrl={group.workerProfileImageUrl}
                          clientId={group.clientId}
                          clientName={group.clientName}
                          showClientName={showClientName}
                        />
                        <Dialog.Title className="mt-2 text-sm font-semibold text-gray-900">
                          Timesheets for{' '}
                          {formatTimesheetDateRange(
                            payPeriod.startDateISO,
                            payPeriod.endDateISO,
                          )}
                        </Dialog.Title>
                      </div>

                      <div
                        className={`flex-1 space-y-3 overflow-y-auto px-6 py-4 ${
                          canSubmit ? 'pb-20' : ''
                        }`}
                      >
                        {group.items.map((item) => {
                          const key = missingTimesheetKey(item);
                          return (
                            <MissingTimesheetCard
                              key={key}
                              item={item}
                              times={resolveMissingTimesheetTimes(
                                item,
                                editedTimesByKey[key],
                              )}
                              canEdit={canSubmit && !isSubmitting}
                              isEditing={editingShiftKey === key}
                              didNotWork={didNotWorkKeys.has(key)}
                              onStartEdit={() => setEditingShiftKey(key)}
                              onCancelEdit={() => setEditingShiftKey(null)}
                              onToggleDidNotWork={() => {
                                setEditingShiftKey(null);
                                setDidNotWorkKeys((current) => {
                                  const next = new Set(current);
                                  if (next.has(key)) {
                                    next.delete(key);
                                  } else {
                                    next.add(key);
                                  }
                                  return next;
                                });
                              }}
                              onSave={(times) => {
                                setEditedTimesByKey((current) => ({
                                  ...current,
                                  [key]: times,
                                }));
                                setEditingShiftKey(null);
                              }}
                            />
                          );
                        })}
                      </div>

                      {canSubmit && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-transparent px-6 pb-4">
                          <Button
                            type="button"
                            variant="primary"
                            disabled={
                              isSubmitting ||
                              editingShiftKey !== null ||
                              submittableItems.length === 0
                            }
                            isLoading={isSubmitting}
                            onClick={() => {
                              void submitAll();
                            }}
                            className="pointer-events-auto !w-full !rounded-lg !py-2 !text-sm !font-medium"
                          >
                            Submit {pluralize('timesheet', group.items.length)}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MissingTimesheetDrawer;
